import React, { useRef, useEffect, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const libraries = ['places'];

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Digite o endereço ou pesquise no mapa",
  onCoordinatesChange,
  className = ""
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || '');
  const [isLocating, setIsLocating] = useState(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;

    // Cleanup previous autocomplete
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    }

    // Create new autocomplete
    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'br' },
      fields: ['address_components', 'formatted_address', 'geometry', 'name'],
      types: ['address']
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      
      if (!place.geometry) {
        console.log('No geometry for this place');
        return;
      }

      // Extract address components
      const addressData = {
        formatted: place.formatted_address || '',
        street: '',
        number: '',
        neighborhood: '',
        city: '',
        state: '',
        cep: '',
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      };

      place.address_components?.forEach(component => {
        const types = component.types;
        if (types.includes('street_number')) {
          addressData.number = component.long_name;
        }
        if (types.includes('route')) {
          addressData.street = component.long_name;
        }
        if (types.includes('sublocality_level_1') || types.includes('sublocality')) {
          addressData.neighborhood = component.long_name;
        }
        if (types.includes('administrative_area_level_2')) {
          addressData.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          addressData.state = component.short_name;
        }
        if (types.includes('postal_code')) {
          addressData.cep = component.long_name.replace(/\D/g, '');
        }
      });

      setInputValue(place.formatted_address || '');
      onChange(addressData);
      
      if (onCoordinatesChange && addressData.latitude && addressData.longitude) {
        onCoordinatesChange(addressData.latitude, addressData.longitude);
      }
    });

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded, onChange, onCoordinatesChange]);

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocalização não suportada. Por favor, digite o endereço manualmente ou use a busca por CEP.');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        if (isLoaded) {
          // Reverse geocode
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat: latitude, lng: longitude } },
            (results, status) => {
              setIsLocating(false);
              if (status === 'OK' && results[0]) {
                const place = results[0];
                
                const addressData = {
                  formatted: place.formatted_address || '',
                  street: '',
                  number: '',
                  neighborhood: '',
                  city: '',
                  state: '',
                  cep: '',
                  latitude,
                  longitude
                };

                place.address_components?.forEach(component => {
                  const types = component.types;
                  if (types.includes('street_number')) {
                    addressData.number = component.long_name;
                  }
                  if (types.includes('route')) {
                    addressData.street = component.long_name;
                  }
                  if (types.includes('sublocality_level_1') || types.includes('sublocality')) {
                    addressData.neighborhood = component.long_name;
                  }
                  if (types.includes('administrative_area_level_2')) {
                    addressData.city = component.long_name;
                  }
                  if (types.includes('administrative_area_level_1')) {
                    addressData.state = component.short_name;
                  }
                  if (types.includes('postal_code')) {
                    addressData.cep = component.long_name.replace(/\D/g, '');
                  }
                });

                setInputValue(place.formatted_address || '');
                onChange(addressData);
                toast.success('Localização encontrada!');
                
                if (onCoordinatesChange) {
                  onCoordinatesChange(latitude, longitude);
                }
              } else {
                // Geocoding failed but we still have coordinates - use them
                console.warn('Geocoding failed with status:', status);
                toast.error('Não foi possível converter as coordenadas em endereço. Digite o endereço manualmente ou busque pelo CEP.');
              }
            }
          );
        } else {
          setIsLocating(false);
          toast.error('Google Maps ainda está carregando. Tente novamente em alguns segundos.');
        }
      },
      (error) => {
        setIsLocating(false);
        console.error('Error getting location:', error);
        
        // Provide specific error messages
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Permissão de localização negada. Habilite nas configurações do navegador ou digite o endereço manualmente.', { duration: 6000 });
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Localização não disponível no momento. Digite o endereço manualmente ou busque pelo CEP.', { duration: 5000 });
            break;
          case error.TIMEOUT:
            toast.error('Sua localização está demorando para ser encontrada. Tente novamente ou digite o endereço/CEP manualmente.', { duration: 6000 });
            break;
          default:
            toast.error('Não foi possível obter sua localização. Digite o endereço manualmente ou busque pelo CEP.', { duration: 5000 });
        }
      },
      { enableHighAccuracy: false, timeout: 30000, maximumAge: 60000 }
    );
  };

  if (loadError) {
    console.error('Google Maps load error:', loadError);
    // Check if it's an API key error
    const isApiKeyError = loadError.message?.includes('ApiNotActivatedMapError') || 
                          loadError.message?.includes('InvalidKeyMapError') ||
                          loadError.message?.includes('RefererNotAllowedMapError');
    
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              placeholder="Busca por mapa indisponível"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="pl-10 bg-[#0f0f15] border-white/10 text-white"
              disabled
            />
          </div>
        </div>
        <p className="text-xs text-green-400">
          ✅ Use a <strong>busca por CEP</strong> abaixo ou digite o endereço nos campos.
        </p>
        {isApiKeyError && (
          <p className="text-xs text-yellow-400">
            ⚠️ A API do Google Maps precisa ser habilitada. Entre em contato com o suporte.
          </p>
        )}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`flex gap-2 ${className}`}>
        <Input
          placeholder="Carregando..."
          disabled
          className="flex-1 bg-[#0f0f15] border-white/10 text-white"
        />
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="pl-10 bg-[#0f0f15] border-white/10 text-white"
          />
        </div>
        <Button
          type="button"
          onClick={getCurrentLocation}
          disabled={isLocating}
          className="btn-secondary whitespace-nowrap"
          title="Usar minha localização atual (requer permissão do navegador)"
        >
          {isLocating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Navigation className="w-4 h-4" />
          )}
        </Button>
      </div>
      <p className="text-xs text-white/40">
        Digite o endereço no campo acima para buscar pelo Google Maps, ou clique no ícone de navegação para usar sua localização atual.
      </p>
    </div>
  );
};

export default AddressAutocomplete;
